import axios from 'axios';
import bookingDataStorage from "../data-storage";

declare let booking_object: { rest_urls: { reset_booking: string } };

export default class CheckTime{

    private readonly apiUrl: string;

    private readonly elDate: string = '';

    private readonly expirationMinutes: number = 10;

    private countdownInterval: NodeJS.Timeout | null = null;

    private countdown_booking: HTMLElement | null;

    private readonly countdownElements: {
        mins: HTMLSpanElement | null;
        secs: HTMLSpanElement | null;
    };

    private modDetailsBooking = new bookingDataStorage();

    /**
     * Constructor
     * @param elDate 
     * @param expirationMinutes 
     * @param order_uuid 
     * @returns 
     */
    constructor(elDate: string = '', expirationMinutes: number = 10) {

        this.expirationMinutes = expirationMinutes;

        this.countdown_booking = document.querySelector('#countdown-booking');

        // Store references to countdown elements if they exist
        this.countdownElements = {
            mins: document.querySelector("#countdown-mins"),
            secs: document.querySelector("#countdown-secs"),
        };

        this.apiUrl = booking_object.rest_urls.reset_booking;

        if(this.countdown_booking === null) return;

        const get_storage = this.modDetailsBooking.getLocalStorage();

        // Return null if no data is found
        if(get_storage === null && elDate === ''){
            
            return;

        };
        
        this.elDate = get_storage?.created_at ? get_storage.created_at : elDate;

    }

    /**
     * Starts the countdown timer and handles expiration logic.
     */
    async startCountDown(): Promise<void> {
        
        const createdAt = new Date(this.elDate).getTime();

        if(isNaN(createdAt)){
            
            // Stop execution if the date is invalid
            return; 

        }else{
            
            this.countdown_booking?.classList.remove('hidden');
            this.countdown_booking?.classList.add('flex');

        }

        // Convert minutes to milliseconds
        const expirationTime = this.expirationMinutes * 60 * 1000; 

        /**
         * Updates the countdown display and checks expiration status.
         */
        const updateCountdown = async (): Promise<void> => {
            
            const now = Date.now();
            const remainingTime = createdAt + expirationTime - now;

            if(remainingTime <= 0){
                
                await this.handleExpiration();
                return;

            }

            // Update the countdown display
            this.updateCountdownDisplay(remainingTime);
            
        };

        // Execute the first update immediately
        await updateCountdown();

        // Start interval for countdown updates
        this.countdownInterval = setInterval(updateCountdown, 1000);

    }

    /**
     * Calculates and updates the countdown display elements.
     * @param remainingTime Time left in milliseconds.
     */
    private updateCountdownDisplay(remainingTime: number): void {

        const days = Math.floor(remainingTime / (1000 * 60 * 60 * 24));
        const hours = Math.floor((remainingTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);

        const formattedMinutes = minutes.toString().padStart(2, '0');
        const formattedSeconds = seconds.toString().padStart(2, '0');

        // Update countdown elements only if they exist
        if(this.countdownElements.mins) this.countdownElements.mins.innerText = formattedMinutes;
        if(this.countdownElements.secs) this.countdownElements.secs.innerText = formattedSeconds;

    }

    /**
     * Handles actions when the countdown expires.
     */
    private async handleExpiration(): Promise<void> {

        try{
            
            const get_storage = this.modDetailsBooking.getLocalStorage();

            // Convert the object to a JSON string
            const postData = JSON.stringify({ fields: { order_id: get_storage?.order_uuid } });

            await this.sendPostRequest(postData);

            // Remove stored data
            this.modDetailsBooking.removeStoredValue();

            // Remove step in local storage
            this.modDetailsBooking.removeStorageStep();

            // Stop the countdown interval
            if(this.countdownInterval){
                clearInterval(this.countdownInterval);
                this.countdownInterval = null;
            }

            // Reload the page after a short delay
            location.reload();

        }catch (error){
            
            console.error("Error while sending post request:", error);

        }

    }

    /**
     * Sends a POST request to the API.
     * @param data The request payload as a JSON string.
     * @returns {Promise<any>} API response.
     */
    private async sendPostRequest(data: string): Promise<any> {
        
        try{
            
            const response = await axios.post(this.apiUrl, data, {
                headers: {
                    'Content-Type': 'application/json',
                },
                validateStatus: status => status === 200,
            });

            return response.data;

        }catch (error){
            
            console.error("Failed to send POST request:", error);
            throw error;

        }

    }

}
